/**
 * Template History Component
 * Storico degli invii template con tracking delivery/read
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';

const TemplateHistory = () => {
  const { t } = useTranslation();
  
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, delivered: 0, read: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (templateFilter) params.append('template_id', templateFilter);
      
      const response = await api.get(`/api/v1/templates/history?${params.toString()}`);
      setHistory(response.data.sends);
      setStats({
        total: response.data.total,
        sent: response.data.sent,
        delivered: response.data.delivered,
        read: response.data.read,
        failed: response.data.failed
      });
    } catch (err) {
      setError(getApiErrorMessage(err, t('templates.history.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, templateFilter, t]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'sent': return '📤';
      case 'delivered': return '✅';
      case 'read': return '👀';
      case 'failed': return '❌';
      default: return '•';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'sent': return '#3b82f6';
      case 'delivered': return '#10b981';
      case 'read': return '#25D366';
      case 'failed': return '#ef4444';
      default: return '#666';
    }
  };

  // Calculate delivery rate
  const deliveryRate = stats.total > 0 
    ? Math.round((stats.delivered + stats.read) / stats.total * 100) 
    : 0;
  
  const readRate = (stats.delivered + stats.read) > 0 
    ? Math.round(stats.read / (stats.delivered + stats.read) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="history-list">
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{stats.total}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('templates.history.stats.total')}</div>
        </div>
        <div style={{ padding: '1rem', background: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{stats.sent}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('templates.history.stats.sent')}</div>
        </div>
        <div style={{ padding: '1rem', background: '#dcfce7', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{stats.delivered}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('templates.history.stats.delivered')}</div>
        </div>
        <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#25D366' }}>{stats.read}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('templates.history.stats.read')}</div>
        </div>
        <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{stats.failed}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('templates.history.stats.failed')}</div>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
        <div>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>{t('templates.history.deliveryRate')}: </span>
          <strong style={{ color: deliveryRate >= 90 ? '#10b981' : deliveryRate >= 70 ? '#f59e0b' : '#ef4444' }}>
            {deliveryRate}%
          </strong>
        </div>
        <div>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>{t('templates.history.readRate')}: </span>
          <strong style={{ color: readRate >= 50 ? '#25D366' : '#f59e0b' }}>
            {readRate}%
          </strong>
        </div>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">{t('templates.history.filters.allStatus')}</option>
          <option value="sent">{t('templates.history.filters.sent')}</option>
          <option value="delivered">{t('templates.history.filters.delivered')}</option>
          <option value="read">{t('templates.history.filters.read')}</option>
          <option value="failed">{t('templates.history.filters.failed')}</option>
        </select>
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* List */}
      {history.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📊</div>
          <h3>{t('templates.history.empty.title')}</h3>
          <p>{t('templates.history.empty.description')}</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="template-list-header" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr' }}>
            <span>{t('templates.history.list.template')}</span>
            <span>{t('templates.history.list.recipient')}</span>
            <span>{t('templates.history.list.sentAt')}</span>
            <span>{t('templates.history.list.status')}</span>
            <span>{t('templates.history.list.tracking')}</span>
          </div>

          {/* Items */}
          {history.map((item) => (
            <div key={item.id} className="history-item" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{item.template_name || `Template #${item.template_id}`}</div>
                {item.variables_used && (
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    {Object.entries(item.variables_used).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' • ')}
                  </div>
                )}
              </div>
              
              <div style={{ fontSize: '0.9rem' }}>
                {item.recipient_phone}
              </div>
              
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                {formatDateTime(item.sent_at)}
              </div>
              
              <div className={`history-status ${item.status}`}>
                <span className="icon">{getStatusIcon(item.status)}</span>
                <span style={{ color: getStatusColor(item.status) }}>
                  {t(`templates.history.status.${item.status}`)}
                </span>
              </div>
              
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {item.delivered_at && (
                  <div>✅ {formatDateTime(item.delivered_at)}</div>
                )}
                {item.read_at && (
                  <div>👀 {formatDateTime(item.read_at)}</div>
                )}
                {item.error_message && (
                  <div style={{ color: '#ef4444' }} title={item.error_message}>
                    ❌ Error
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default TemplateHistory;
